import { supabase } from '../../lib/supabase'
import { todayIso } from '../reports/reportDates'
import type { ExceptionState } from './availabilityStore'

// muster.open_shift_claim_state has no 'returned' (a claim is approved or
// denied), so it sits inside the exception vocabulary the status chip reads.
export type ClaimState = Extract<ExceptionState, 'pending' | 'approved' | 'denied'>

export type OpenShiftForMe = {
  id: string
  date: string
  // Absent for a shift an agent gave away that belongs to no shift template.
  shiftName?: string
  startTime: string
  endTime: string
  assignmentName?: string
  note?: string
  // The teammate giving the shift away, when it is a giveaway.
  releasedByName?: string
}

// A shift the agent has offered to the team.
export type MyGiveaway = {
  id: string
  date: string
  startTime: string
  endTime: string
  assignmentName?: string
  note?: string
  state: 'open' | 'filled' | 'cancelled'
}

export type MyClaim = {
  id: string
  openShiftId: string
  date: string
  shiftName?: string
  startTime: string
  endTime: string
  assignmentName?: string
  state: ClaimState
  decisionComment?: string
}

export type ClaimResult = { ok: true } | { ok: false; message: string }

const FAILED = 'The change could not be saved. Try again.'
const hourMinute = (value: string) => value.slice(0, 5)

async function getMyEmployeeId(userId: string): Promise<string | null> {
  const { data } = await supabase.from('employees').select('id').eq('profile_id', userId).maybeSingle()
  return data?.id ?? null
}

type OpenShiftRow = {
  id: string
  date: string
  start_time: string
  end_time: string
  note: string | null
  state?: 'open' | 'filled' | 'cancelled'
  released_by_employee_id?: string | null
  released_by_name?: string | null
  shift_templates: { name: string } | null
  assignments: { name: string } | null
}

// The open slots of the agent's own team from today on, soonest first, without
// the shifts the agent is giving away themselves: those are theirs to withdraw,
// not to claim. Row-level security keeps every other team's out.
export async function getOpenShiftsForMe(userId: string): Promise<OpenShiftForMe[]> {
  const employeeId = await getMyEmployeeId(userId)
  const { data, error } = await supabase
    .from('open_shifts')
    .select('id, date, start_time, end_time, note, released_by_employee_id, released_by_name, shift_templates(name), assignments(name)')
    .eq('state', 'open')
    .gte('date', todayIso())
    .order('date', { ascending: true })
  if (error || !data) return []
  return (data as unknown as OpenShiftRow[])
    .filter((row) => !employeeId || row.released_by_employee_id !== employeeId)
    .map((row) => ({
      id: row.id,
      date: row.date,
      shiftName: row.shift_templates?.name,
      startTime: hourMinute(row.start_time),
      endTime: hourMinute(row.end_time),
      assignmentName: row.assignments?.name ?? undefined,
      note: row.note ?? undefined,
      releasedByName: row.released_by_name ?? undefined,
    }))
}

// The shifts the agent has offered to the team, soonest first.
export async function getMyGiveaways(userId: string): Promise<MyGiveaway[]> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return []
  const { data, error } = await supabase
    .from('open_shifts')
    .select('id, date, start_time, end_time, note, state, assignments(name)')
    .eq('released_by_employee_id', employeeId)
    .gte('date', todayIso())
    .order('date', { ascending: true })
  if (error || !data) return []
  return (data as unknown as OpenShiftRow[]).map((row) => ({
    id: row.id,
    date: row.date,
    startTime: hourMinute(row.start_time),
    endTime: hourMinute(row.end_time),
    assignmentName: row.assignments?.name ?? undefined,
    note: row.note ?? undefined,
    state: row.state ?? 'open',
  }))
}

type ClaimRow = {
  id: string
  open_shift_id: string
  state: ClaimState
  open_shifts: OpenShiftRow | null
  decisions: { comment: string | null; decided_at: string }[] | null
}

// The agent's own claims, newest slot first, each with the comment on its
// latest decision.
export async function getMyClaims(userId: string): Promise<MyClaim[]> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return []
  const { data, error } = await supabase
    .from('open_shift_claims')
    .select('id, open_shift_id, state, open_shifts(date, start_time, end_time, shift_templates(name), assignments(name)), decisions(comment, decided_at)')
    .eq('employee_id', employeeId)
  if (error || !data) return []

  return (data as unknown as ClaimRow[])
    .flatMap((row) => {
      if (!row.open_shifts) return []
      const decision = (row.decisions ?? []).reduce<{ comment: string | null; decided_at: string } | undefined>(
        (latest, current) => (!latest || current.decided_at > latest.decided_at ? current : latest),
        undefined,
      )
      return [
        {
          id: row.id,
          openShiftId: row.open_shift_id,
          date: row.open_shifts.date,
          shiftName: row.open_shifts.shift_templates?.name,
          startTime: hourMinute(row.open_shifts.start_time),
          endTime: hourMinute(row.open_shifts.end_time),
          assignmentName: row.open_shifts.assignments?.name ?? undefined,
          state: row.state,
          decisionComment: decision?.comment ?? undefined,
        },
      ]
    })
    .sort((a, b) => b.date.localeCompare(a.date))
}

// Claims a slot, pending until a supervisor decides it.
export async function claimOpenShift(userId: string, openShiftId: string, comment?: string): Promise<ClaimResult> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return { ok: false, message: FAILED }
  const { error } = await supabase.from('open_shift_claims').insert({
    open_shift_id: openShiftId,
    employee_id: employeeId,
    comment: comment?.trim() ? comment.trim() : null,
    state: 'pending',
    created_by: userId,
  })
  if (!error) return { ok: true }
  // 23505 is a claim already made for this slot.
  return { ok: false, message: error.code === '23505' ? 'You have already claimed this shift.' : 'That shift can no longer be claimed.' }
}

const GIVE_AWAY_REFUSED =
  'That shift cannot be given away. It has to be one of your work shifts on a published pay period, from today on.'

// Offers one of the agent's work shifts to the team. They keep it until a
// teammate's claim is approved. Row-level security holds the rules, and the
// shift's hours and assignment are taken from the schedule itself, so none are
// sent here.
export async function giveAwayShift(userId: string, date: string, message?: string): Promise<ClaimResult> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return { ok: false, message: FAILED }
  const today = todayIso()
  const { data: placement } = await supabase
    .from('employee_assignments')
    .select('team_id')
    .eq('employee_id', employeeId)
    .eq('active', true)
    .lte('effective_from', today)
    .or(`effective_to.is.null,effective_to.gte.${today}`)
    .maybeSingle()
  if (!placement) return { ok: false, message: FAILED }

  const { error } = await supabase.from('open_shifts').insert({
    team_id: placement.team_id,
    date,
    released_by_employee_id: employeeId,
    note: message?.trim() ? message.trim() : null,
    state: 'open',
    created_by: userId,
  })
  if (!error) return { ok: true }
  return { ok: false, message: error.code === '42501' || error.code === '23514' ? GIVE_AWAY_REFUSED : FAILED }
}

// Withdraws an offer that no one has taken yet; its claims are denied.
export async function withdrawGiveaway(userId: string, id: string): Promise<ClaimResult> {
  const { error } = await supabase.from('open_shifts').update({ state: 'cancelled', updated_by: userId }).eq('id', id)
  return error ? { ok: false, message: FAILED } : { ok: true }
}

// Withdraws a claim that has not been decided.
export async function withdrawClaim(id: string): Promise<ClaimResult> {
  const { error } = await supabase.from('open_shift_claims').delete().eq('id', id)
  return error ? { ok: false, message: FAILED } : { ok: true }
}
